// The Meta dashboard's DIMENSIONAL widget data — PURE.
//
// ⚠️ THE DIMENSIONS ARE THE ONES THE BREAKDOWN EXPLORER ALREADY GROUPS BY —
// Account, Campaign, Ad set, Ad — reusing the SAME `/breakdown` endpoint the
// explorer uses rather than building a "metric by arbitrary dimension" query.
// Brand / AM / group / industry are FILTERS on this dashboard, not groupings, so
// they are deliberately not offered; adding them would mean a new server
// aggregation, which this phase does not build.
//
// ⚠️⚠️ THE MIXED-CURRENCY RULE, NOW CONDITIONAL ON THE DISPLAY CURRENCY.
// A breakdown row carries its own native `currency` and the endpoint never sums
// across currencies. So:
//
//   · NATIVE mode — unchanged. A money metric whose rows span several currencies
//     reports `mixedCurrency` and the widget REFUSES to plot: summing AUD into
//     NZD would put a number on a pie that exists in no currency, and a pie is
//     exactly the shape that hides it (the slices still total a tidy 100%).
//   · DISPLAY-CURRENCY mode — every money row is CONVERTED at the snapshot rate
//     first, so there genuinely is one currency and the widget plots. That is the
//     deliberate, rated blend, honest for precisely the reason the raw one isn't.
//
// ⚠️ The refusal did not go away — it moved behind a condition. A RAW AUD+NZD sum
// remains impossible in BOTH modes, which is the invariant the mutation tests
// pin. Counts and ratios are currency-agnostic and pass straight through either
// way.
//
// Metric values are computed with the SAME `TrendMetric.value()` the hero cards
// and every chart use, from each row's additive totals — so a ratio (CPL, CTR)
// is recomputed per dimension value from that value's own numerator and
// denominator, never averaged.
//
// ⚠️ THE `ReportingWidgets` IMPORT IS TYPE-ONLY, and must stay that way. This
// module is reached from the PREFS module (which passes its dimension predicate
// to the widget decoder), so a runtime import of the component layer here would
// re-create exactly the circular-init edge googleAdsChartCatalog.ts documents —
// the one that white-screened the app. `import type` is erased, so there is no
// runtime edge at all.

import type {
  ReportingBreakdownRow,
  ReportingDimension,
} from '../../types/reporting';
import type { WidgetDimensionalData, WidgetSlice } from './ReportingWidgets';
import { resolveTrendMetric, type TrendTotals } from './trendSeries';
import type { FxRateSnapshot } from '../../types/fxRates';
import { convertAmount, isNativeDisplay } from '../../lib/reporting/currencyConversion';

export const META_WIDGET_DIMENSIONS: Array<{ key: ReportingDimension; label: string }> = [
  { key: 'account', label: 'Account' },
  { key: 'campaign', label: 'Campaign' },
  { key: 'adset', label: 'Ad set' },
  { key: 'ad', label: 'Ad' },
];

export function isMetaWidgetDimension(key: string): key is ReportingDimension {
  return META_WIDGET_DIMENSIONS.some((d) => d.key === key);
}

/** A breakdown row's identity for the dimension being split by. */
function identify(
  row: ReportingBreakdownRow,
  dimension: ReportingDimension,
): { key: string; label: string } {
  switch (dimension) {
    case 'account':
      // ⚠️ Unattributed rows have no account. They are kept as an explicit
      // bucket rather than dropped — dropping them would make the widget's
      // total quietly fall below the KPI tiles above it, the same reason the
      // Google tree carries its "needs match token" node.
      return { key: row.accountId ?? '__unattributed__', label: row.accountName ?? 'Unattributed' };
    case 'campaign':
      return { key: row.campaignId ?? '—', label: row.campaignName ?? '—' };
    case 'adset':
      return { key: row.adsetId ?? '—', label: row.adsetName ?? '—' };
    case 'ad':
      return { key: row.adId ?? '—', label: row.adName ?? '—' };
  }
}

/** One breakdown row's additive totals, in the shape every metric's value() reads. */
function rowTotals(row: ReportingBreakdownRow): TrendTotals {
  return {
    spend: row.spend,
    metaLeadSpend: row.metaLeadSpend,
    leads: row.totalLeads,
    metaLeads: row.metaLeads,
    websiteLeads: row.websiteLeads,
    impressions: row.impressions,
    linkClicks: row.linkClicks,
  };
}

function addInto(acc: TrendTotals, t: TrendTotals): void {
  acc.spend += t.spend;
  acc.metaLeadSpend += t.metaLeadSpend;
  acc.leads += t.leads;
  acc.metaLeads += t.metaLeads;
  acc.websiteLeads += t.websiteLeads;
  acc.impressions += t.impressions;
  acc.linkClicks += t.linkClicks;
}

/**
 * A row's totals with its MONEY fields converted into the display currency.
 *
 * ⚠️ No rate → 0, never the native amount. Passing the raw figure through would
 * silently add an NZD number to an AUD total, which is the exact failure the
 * whole per-currency design exists to prevent — an understated total is wrong in
 * a way a reader can investigate; a blended one is wrong invisibly.
 */
function convertRowTotals(
  row: ReportingBreakdownRow,
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
): TrendTotals {
  const base = rowTotals(row);
  const from = row.currency ?? display;
  return {
    ...base,
    spend: convertAmount(base.spend, from, display, snapshot) ?? 0,
    metaLeadSpend: convertAmount(base.metaLeadSpend, from, display, snapshot) ?? 0,
  };
}

const ZERO: TrendTotals = {
  spend: 0,
  metaLeadSpend: 0,
  leads: 0,
  metaLeads: 0,
  websiteLeads: 0,
  impressions: 0,
  linkClicks: 0,
};

/**
 * Split a metric by one of the breakdown dimensions.
 *
 * Rows are grouped by the dimension's identity and their ADDITIVE totals summed;
 * the metric's value is then computed from those sums, so a ratio is recomputed
 * per dimension value rather than averaged across rows.
 */
export function buildMetaDimensionalData(input: {
  metricKey: string;
  dimension: ReportingDimension;
  rows: readonly ReportingBreakdownRow[] | undefined;
  loading: boolean;
  unavailable?: string;
  /** 'native' (the default) or the currency every money row is converted into. */
  display?: string;
  snapshot?: FxRateSnapshot | null;
}): WidgetDimensionalData {
  const metric = resolveTrendMetric(input.metricKey);
  const rows = input.rows ?? [];
  const display = input.display ?? '';
  const converting = !isNativeDisplay(display);

  const byKey = new Map<string, { label: string; totals: TrendTotals }>();
  const currencies = new Set<string>();
  for (const row of rows) {
    if (row.currency) currencies.add(row.currency);
    const { key, label } = identify(row, input.dimension);
    let entry = byKey.get(key);
    if (!entry) {
      entry = { label, totals: { ...ZERO } };
      byKey.set(key, entry);
    }
    // ⚠️ Converting the ROW'S money totals BEFORE they are summed is what makes
    // the blend rated rather than raw. A row we have no rate for contributes
    // ZERO money (see convertRowTotals) rather than its native figure.
    addInto(
      entry.totals,
      converting ? convertRowTotals(row, display, input.snapshot) : rowTotals(row),
    );
  }

  const list = [...currencies].sort();
  const slices: WidgetSlice[] = [...byKey.entries()].map(([key, entry]) => ({
    key,
    label: entry.label,
    value: metric.value(entry.totals),
  }));

  return {
    slices,
    currency: converting ? display : list.length === 1 ? list[0]! : null,
    // ⚠️ The refusal is scoped to MONEY. A click count is a click count whichever
    // currency paid for it, so narrowing counts and ratios would be over-strict
    // and would make the rule read as arbitrary rather than principled.
    //
    // ⚠️⚠️ …and it applies only in NATIVE mode. With a display currency active
    // every money row has ALREADY been converted above, so there genuinely is one
    // currency and refusing would withhold a figure we can legitimately produce.
    // The refusal did not go away — it moved behind a condition, and a RAW
    // AUD+NZD sum remains impossible in both modes.
    mixedCurrency: !converting && metric.format === 'money' && list.length > 1,
    currencies: list,
    format: metric.format,
    unavailable: input.unavailable,
    loading: input.loading,
  };
}
